import { useEffect } from "react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import PublicKpiStrip from "@/components/landing/PublicKpiStrip";
import { BRAND } from "@/lib/brand";
import { SITE_URL } from "@/lib/siteConfig";

const TITLE = "Trust — Myelina Health";
const DESC =
  "Where your data lives, who touches it, how long we keep it, and how to reach us if something looks wrong. Maintained by Myelina Health.";

const Trust = () => {
  useEffect(() => {
    document.title = TITLE;
    const setMeta = (name: string, content: string, attr: "name" | "property" = "name") => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta("description", DESC);
    setMeta("og:title", TITLE, "property");
    setMeta("og:description", DESC, "property");
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${SITE_URL}/trust`);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-6 sm:px-10 lg:px-16 py-24 max-w-3xl">
        <h1 className="font-display text-5xl md:text-6xl tracking-tight mb-6">Trust</h1>
        <p className="lead-italic text-foreground/80 mb-4">
          Where your data lives, who touches it, and how to reach us.
        </p>
        <p className="text-sm text-muted-foreground mb-12">
          This page is maintained by {BRAND.legalName} to answer the security and privacy
          questions we hear most. It describes app-visible controls and our current practices —
          not an independent certification.
        </p>

        <section className="space-y-10 text-[16px] leading-[1.8] text-foreground/85">
          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">Data residency</h2>
            <p>
              Your account, check-ins, reflections, and AI conversation history are stored in the
              United States (AWS <code className="text-sm">us-east-1</code>) on our managed
              Postgres backend. Backups stay in the same region.
            </p>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">Subprocessors</h2>
            <p>These are the third parties that touch your data so the product can work:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>
                <strong>Supabase</strong> — managed Postgres, auth, and edge functions
                (<code className="text-sm">us-east-1</code>).
              </li>
              <li>
                <strong>Lovable</strong> — application hosting and delivery.
              </li>
              <li>
                <strong>Emailit</strong> — outbound email (account, care-team share, and
                transactional notifications).
              </li>
              <li>
                <strong>Model providers used by Myelina</strong> — prompts and responses are sent
                under contracts that forbid retention for training. We send the minimum context
                required to answer.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">Retention &amp; deletion</h2>
            <p>
              You can delete your account from Settings. Deletion purges every related row —
              profile, check-ins, reflections, medications, wearable imports, AI conversations,
              buddy links, email logs, subscription records — within minutes, not days. Backups
              age out on a 30-day rolling window.
            </p>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">What we never do</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>We never sell your data.</li>
              <li>We never share identifiable data with insurers, employers, or advertisers.</li>
              <li>We never use your check-ins to train public AI models.</li>
              <li>We never track you across other sites with our cookies.</li>
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">Reporting a vulnerability</h2>
            <p>
              Email <a className="underline" href="mailto:security@myelina.health">security@myelina.health</a>{" "}
              or read our{" "}
              <a className="underline" href="/.well-known/security.txt">security.txt</a>. We
              respond within 48 hours and credit responsible disclosure in our{" "}
              <a className="underline" href="/changelog">changelog</a>.
            </p>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">Compliance posture</h2>
            <p>
              Myelina Health is a wellness tool, not a medical device, and is not a HIPAA-covered
              entity. Our architecture aligns with GDPR (EU/UK) and CCPA (California) principles:
              data minimization, purpose limitation, row-level access control, and user-initiated
              deletion. For the full picture of encryption and row-level security, see{" "}
              <a className="underline" href="/security">Security &amp; privacy</a>.
            </p>
          </div>

          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">General contact</h2>
            <p>
              For anything non-security, email{" "}
              <a className="underline" href="mailto:hello@myelina.health">hello@myelina.health</a>.
            </p>
          </div>
        </section>

      </main>
      <PublicKpiStrip />
      <Footer />
    </div>
  );
};

export default Trust;
